// Validate URL attribution; returning-visit storage is used only with analytics consent.
export function hasMeasurementConsent(cookie, privacy = {}) {
  return /(?:^|;\s*)ix_consent=accept(?:;|$)/.test(cookie)
    && /(?:^|;\s*)ix_consent_version=2(?:;|$)/.test(cookie)
    && privacy.doNotTrack !== '1' && privacy.globalPrivacyControl !== true;
}

/** Booking context and ad completion must agree, including consented return visits. */
export function isPartnershipBooking(pathname, search, campaign = readCampaignParameters(search)) {
  const sources = new URLSearchParams(search).getAll('source');
  return /^\/book(?:\/intro-30)?\/?$/.test(pathname)
    && ((sources.length === 1 && sources[0] === 'ai-implementation-partnership')
      || campaign.utm_campaign === 'ai-partnership-202610');
}

/** Stable, non-personal provider deduplication key; never hash a reference/cancel URL. */
export async function bookingConversionId(bookingId, cryptoProvider = globalThis.crypto) {
  if (typeof bookingId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(bookingId)
    || !cryptoProvider?.subtle) return null;
  const digest = await cryptoProvider.subtle.digest('SHA-256',
    new TextEncoder().encode('ix-booking-conversion:v1:' + bookingId));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function readCampaignParameters(search) {
  const params = new URLSearchParams(search);
  const campaign = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const values = params.getAll(key);
    const pattern = key === 'utm_term' ? /^[a-zA-Z0-9][a-zA-Z0-9 _-]{0,119}$/ : /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,119}$/;
    if (values.length === 1 && pattern.test(values[0])) {
      campaign[key] = values[0];
    }
  }
  return campaign;
}

export function campaignLink(href, sourceUrl) {
  const source = new URL(sourceUrl);
  const destination = new URL(href, source);
  if (destination.origin !== source.origin) return href;
  for (const [key, value] of Object.entries(readCampaignParameters(source.search))) {
    destination.searchParams.set(key, value);
  }
  return destination.pathname + destination.search + destination.hash;
}

export const CAMPAIGN_STORAGE_KEY = 'ix.campaign.v1';
const CAMPAIGN_TTL = 30 * 24 * 60 * 60 * 1000;
/** Last tagged visit, retained for 30 days only after the existing consent choice. */
export function resolveCampaignParameters(search, storage, consent, now = Date.now()) {
  const current = readCampaignParameters(search);
  try {
    if (!consent) { storage?.removeItem(CAMPAIGN_STORAGE_KEY); return current; }
    if (current.utm_source) {
      storage?.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify({ campaign: current, capturedAt: now }));
      return current;
    }
    // Partial explicit tags never silently borrow a different campaign's identity.
    if (Object.keys(current).length) return current;
    const saved = JSON.parse(storage?.getItem(CAMPAIGN_STORAGE_KEY) || 'null');
    if (saved && Number.isFinite(saved.capturedAt) && now >= saved.capturedAt && now - saved.capturedAt < CAMPAIGN_TTL) {
      return readCampaignParameters(new URLSearchParams(saved.campaign).toString());
    }
    storage?.removeItem(CAMPAIGN_STORAGE_KEY);
  } catch { /* Storage denial must not block booking or navigation. */ }
  return current;
}
